import React, {useEffect, useMemo, useState} from 'react'
import {Stack, Typography} from '@mui/material'
import Table from './Table'
import {useTranslation} from 'react-i18next'
import TitlePage from '../customTable/TitlePage'
import iMedia from '../../interface/iMedia'
import {useNavigate} from 'react-router-dom'
import {RootState} from '../../store'
import {useSelector} from 'react-redux'
import MainBtn from '../customTable/MainBtn'
import Tabs from './Tabs'
import ServiseFees from '../../Api/serviseFees'
import SimpleLoading from '../customTable/SimpleLoading'
import PaymentGatewaysApi from '../../Api/paymentGateways'
import ServiceFeesDueCard, {isServiceFeesReminderActive} from './ServiceFeesDueCard'
import {getOldestUnpaidDaysPastDue} from './getOldestUnpaidDaysPastDue'
import PopUp from '../customTable/PopUp'
import PaymentGatewaySelector from '../wallet/components/PaymentGatewaySelector'
import OTPModal from '../wallet/components/OTPModal'
import {Alert} from '@mui/material'
import {handlePaymentInitiationResponse, formatPaymentDisplay, parsePaymentAmount, isBlomUnifiedCheckout, runBlomUnifiedCheckout, toIsoCurrencyCode} from '../../utils/paymentHelpers'

type FlowStep = 'otp' | 'gateway'

export default function ServesFees() {
  const {t} = useTranslation()
  const navagite = useNavigate()
  const bannerInfo = {
    title: t('servesFees'),
    home: t('home'),
    linkHome: '/',
  }
  const [rows, setRows] = useState<iMedia[]>([])
  const [added, setAdded] = useState(false)
  const [switchValue, setSwitchValue] = useState('cash')
  const [refreshKey, setRefreshKey] = useState(0)
  const [unpaidTotal, setUnpaidTotal] = useState(0)
  const [unpaidCurrency, setUnpaidCurrency] = useState('')
  const [isPayingAll, setIsPayingAll] = useState(false)
  const [isResending, setIsResending] = useState(false)
  const [isPaymentEnabled, setIsPaymentEnabled] = useState(false)
  const [flowOpen, setFlowOpen] = useState(false)
  const [flowStep, setFlowStep] = useState<FlowStep>('otp')
  const [signature, setSignature] = useState('')
  const [otpAmountLabel, setOtpAmountLabel] = useState('')
  const [errorMsg, setErrorMsg] = useState('')
  const [reminderTick, setReminderTick] = useState(0)
  const store = useSelector((state: RootState) => state.vender)

  const countryId = Number(localStorage.getItem('country_id') || 0)

  const shouldShowDueCard = useMemo(() => {
    if (isServiceFeesReminderActive()) {
      return false
    }
    const daysPastDue = getOldestUnpaidDaysPastDue(rows)
    return unpaidTotal > 0 && daysPastDue !== null && daysPastDue > 7
  }, [rows, unpaidTotal, reminderTick])

  useEffect(() => {
    if (added) {
      setAdded(false)
    }
  }, [rows])

  useEffect(() => {
    Promise.all([
      PaymentGatewaysApi.isFactoryEnabledForCountry(countryId, 'service_fee', 'PY_WHISH'),
      PaymentGatewaysApi.isFactoryEnabledForCountry(countryId, 'service_fee', 'PY_BLOM_UC'),
      PaymentGatewaysApi.isFactoryEnabledForCountry(countryId, 'service_fee', 'PY_BLOM'),
      PaymentGatewaysApi.isFactoryEnabledForCountry(countryId, 'service_fee', 'PY_OMT'),
    ]).then(([whish, blomUc, blomLegacy, omt]) => {
      setIsPaymentEnabled(!!(whish || blomUc || blomLegacy || omt))
    })
  }, [countryId])

  const canPayAll = store.permissions && store.permissions.includes('services_fees_pay')

  const resetFlow = () => {
    setFlowStep('otp')
    setSignature('')
    setErrorMsg('')
    setOtpAmountLabel('')
  }

  const closeFlow = () => {
    if (isPayingAll) {
      return
    }
    setFlowOpen(false)
    resetFlow()
  }

  const openPayAllFlow = () => {
    if (unpaidTotal <= 0 || isPayingAll) {
      return
    }
    setErrorMsg('')
    ServiseFees.startOtp(
      null,
      (res: any) => {
        const nextSignature = res?.signature ?? res?.data?.signature
        if (!nextSignature) {
          setErrorMsg(String(t('paymentGatewaysLoadError') || 'Unable to start verification'))
          return
        }
        const nextAmount = parsePaymentAmount(res?.amount ?? res?.data?.amount ?? unpaidTotal)
        const nextCurrency = String(
          res?.currency ?? res?.data?.currency ?? (unpaidCurrency || 'USD')
        ).trim()
        setSignature(String(nextSignature))
        setOtpAmountLabel(formatPaymentDisplay(nextAmount, nextCurrency))
        setFlowStep('otp')
        setFlowOpen(true)
      },
      setIsPayingAll
    )
  }

  const handleOtpSubmit = (code: string) => {
    setErrorMsg('')
    ServiseFees.confirmOtp(
      {signature, code},
      () => setFlowStep('gateway'),
      setIsPayingAll
    )
  }

  const handleGatewaySelected = (factoryName: string) => {
    const currency_code = toIsoCurrencyCode(localStorage.getItem('currency'))

    if (isBlomUnifiedCheckout(factoryName)) {
      runBlomUnifiedCheckout(
        {
          purpose: 'service_fee',
          service_fee_id: null,
          currency_code,
        },
        setIsPayingAll
      )
      return
    }

    ServiseFees.payByGateway(
      factoryName,
      null,
      (res: any) => {
        handlePaymentInitiationResponse(res)
      },
      setIsPayingAll,
      {currency_code}
    )
  }

  return (
    <Stack>
      <Stack
        justifyContent={'space-between'}
        direction={{xs: 'column', md: 'row'}}
        gap='12px'
        alignItems={{xs: 'stretch', md: 'center'}}
      >
        <TitlePage info={bannerInfo} />
        <Stack direction={{xs: 'column', sm: 'row'}} gap='12px' alignItems='center'>
          {canPayAll && (
            <Stack direction='row' gap='12px' alignItems='center'>
              {!shouldShowDueCard && (
                <Typography color='var(--bs-text-dark)' fontSize='14px'>
                  {t('unpaidTotal')}:{' '}
                  {`${(unpaidCurrency || '').trim()} ${unpaidTotal.toLocaleString('en-US', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}`.trim()}
                </Typography>
              )}
              {!isPaymentEnabled ? (
                <Typography color='var(--bs-text-dark)' fontSize='13px'>
                  {t('noPaymentMethodAvailable')}
                </Typography>
              ) : isPayingAll ? (
                <SimpleLoading />
              ) : (
                <MainBtn
                  disabled={unpaidTotal <= 0}
                  handelSubmit={openPayAllFlow}
                  bgcolor='#f16645'
                  width='auto'
                  text='payAll'
                />
              )}
            </Stack>
          )}
          {store.permissions && store.permissions.includes('services_fees_value') && (
            <MainBtn
              handelSubmit={() => {
                navagite('values')
              }}
              bgcolor='#f16645'
              width='auto'
              text='serviceFeeValue'
            />
          )}
        </Stack>
      </Stack>

      <ServiceFeesDueCard
        rows={rows}
        unpaidTotal={unpaidTotal}
        unpaidCurrency={unpaidCurrency}
        isPayingAll={isPayingAll}
        isWhishEnabled={isPaymentEnabled}
        canPay={!!canPayAll}
        onPayNow={openPayAllFlow}
        onDismiss={() => setReminderTick((tick) => tick + 1)}
      />

      <div style={{marginTop: '30px'}} className={`card fds sha`}>
        <Stack p='0 30px' mt='25px' direction={'row'}>
          <Tabs value={switchValue} setValue={setSwitchValue} />
        </Stack>

        <Table
          switchValue={switchValue}
          added={added}
          rows={rows}
          setRows={setRows}
          refreshKey={refreshKey}
          isWhishEnabled={isPaymentEnabled}
          isPayingAll={isPayingAll}
          canPay={!!canPayAll}
          onPayNow={openPayAllFlow}
          onUnpaidChange={(total, currency) => {
            setUnpaidTotal(total)
            setUnpaidCurrency(currency)
          }}
        />
      </div>

      <PopUp width='sm' open={flowOpen} setOpen={setFlowOpen} isLoading={isPayingAll}>
        <Stack p={{xs: '16px', sm: '24px'}} gap='12px' bgcolor='#fff'>
          {errorMsg ? <Alert severity='error'>{errorMsg}</Alert> : null}
          {flowStep === 'otp' ? (
            <OTPModal
              signature={signature}
              amountLabel={otpAmountLabel}
              amount={unpaidTotal}
              currency={unpaidCurrency || 'USD'}
              planName={String(t('servesFees') || '')}
              isLoading={isPayingAll}
              isResending={isResending}
              onSubmit={handleOtpSubmit}
              onCancel={closeFlow}
              onResend={() => ServiseFees.resendOtp({signature}, () => {}, setIsResending)}
            />
          ) : (
            <PaymentGatewaySelector
              open={flowOpen && flowStep === 'gateway'}
              onClose={closeFlow}
              amount={unpaidTotal}
              currency={unpaidCurrency || 'USD'}
              purpose='service_fee'
              countryId={countryId}
              referenceId={0}
              isPaying={isPayingAll}
              onGatewaySelected={handleGatewaySelected}
              summary={{
                titleKey: 'completePayment',
                planName: String(t('servesFees') || ''),
                priceLabel: `${(unpaidCurrency || '').trim()} ${unpaidTotal.toLocaleString('en-US', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}`.trim(),
              }}
            />
          )}
        </Stack>
      </PopUp>
    </Stack>
  )
}
